import { useState, useRef, useEffect } from 'react';
import { useVariables } from '@/pages/mco/steps/components/step/email/preview/use-variables';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import {
  Box,
  Flex,
  Grid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Tooltip,
} from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';

export default function EditorCustomVariables({
  config = undefined,
  editor = undefined,
  customOnClick = undefined,
  wrapperProps = {},
}) {
  const [tabIndex, setTabIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const variableValues = useVariables();
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  // Filter out categories that have no values
  const filteredConfig = Object.entries(config || {}).filter(
    ([_category, values]) => Array.isArray(values) && values.length > 0
  );
  const filteredConfigObj = Object.fromEntries(filteredConfig);

  const isVariablesPresent = Object.keys(filteredConfigObj).length > 0;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const popoverWidth = 500; // minW of popover

      const bounds = { top: triggerRect.bottom + 5 };
      if (viewportWidth - triggerRect.right > popoverWidth) {
        bounds['left'] = triggerRect.left - 250; // 250px offset from left
      } else {
        bounds['right'] = viewportWidth - triggerRect.right;
      }

      setPosition(bounds);
    }
  }, [isOpen]);

  if (!config) return null;

  const handleTriggerClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(!isOpen);
    if (tabIndex > Object.keys(config).length - 1) {
      setTabIndex(0);
    }
  };

  // Check if survey.link variable is already used in the editor
  const isVariableUsed = (variable) => {
    if (!editor || variable !== 'survey.link') return false;
    const content = editor.getText();
    return content.includes(`{{${variable}}}`);
  };

  const handleVariableClick = (variable) => (e) => {
    e.stopPropagation();

    // Prevent insertion if survey.link already exists
    if (isVariableUsed(variable)) {
      return;
    }

    if (editor) {
      editor.commands.insertContent(`{{${variable}}}`);
      // Dispatch custom event to notify that a variable was inserted
      document.dispatchEvent(
        new CustomEvent('kula_editor_variable_inserted', {
          detail: {
            type: 'kula_editor_variable_inserted',
            variable,
            editorName: editor.options.element?.id || null,
            editorType: 'default',
          },
        })
      );
    }
    if (customOnClick) {
      customOnClick(`{{${variable}}}`);
    }
    setIsOpen(false);
  };

  const renderTabs = () => {
    return (
      <Tabs index={tabIndex} isLazy variant="unstyled">
        <TabList borderBottom="2px solid #EAEAEA">
          {Object.keys(filteredConfigObj).map((category, index) => (
            <Tab
              key={index}
              tabIndex={tabIndex}
              borderBottom={tabIndex === index && '2px solid #605AFB'}
              borderColor={tabIndex === index && '#605AFB'}
              onClick={() => setTabIndex(index)}
              _focus={{
                boxShadow: 'none',
              }}
            >
              <KText
                color={
                  index === tabIndex
                    ? 'secondary-900 !important'
                    : 'secondary-700 !important'
                }
                fontWeight={index === tabIndex && 'bold'}
                textTransform="capitalize"
                whiteSpace="nowrap"
              >
                {category.replace(/_/g, ' ')}
              </KText>
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {Object.entries(filteredConfigObj).map(
            ([category, values], index) => (
              <TabPanel key={index} minH="250px" maxH="250px" overflow="auto">
                <Flex gap=".5em" flexDir="column">
                  {values.map((variable, idx) => {
                    const isUsed = isVariableUsed(variable);
                    return (
                      <Grid
                        templateColumns="minmax(0, 3fr) minmax(0, 2fr)"
                        gap="8px"
                        w="100%"
                        alignItems="center"
                        cursor={isUsed ? 'not-allowed' : 'pointer'}
                        key={idx}
                        onClick={handleVariableClick(variable)}
                        opacity={isUsed ? 0.5 : 1}
                        pointerEvents={isUsed ? 'none' : 'auto'}
                      >
                        <TruncatedTextWithTooltip
                          toolTipVariant="default"
                          text={`{{${variable}}}`}
                          color={
                            isUsed
                              ? 'secondary-300 !important'
                              : '#605AFB !important'
                          }
                          size="sm-medium !important"
                        />
                        <TruncatedTextWithTooltip
                          toolTipVariant="default"
                          size="sm-medium !important"
                          text={
                            variableValues[category]?.[variable.split('.')[1]]
                          }
                          textAlign="right"
                          color={
                            isUsed
                              ? 'secondary-300 !important'
                              : 'secondary-500 !important'
                          }
                        />
                      </Grid>
                    );
                  })}
                </Flex>
              </TabPanel>
            )
          )}
        </TabPanels>
      </Tabs>
    );
  };

  return (
    <Box position="relative" onClick={(e) => e.stopPropagation()}>
      <Tooltip
        label={
          !isVariablesPresent
            ? 'Select a category to start using variables'
            : ''
        }
      >
        <KText
          display={'flex'}
          gap="4px"
          alignItems="center"
          ref={triggerRef}
          pt="5px"
          minW="max-content"
          cursor="pointer"
          onClick={!isVariablesPresent ? () => {} : handleTriggerClick}
          {...wrapperProps}
        >
          {`{ }  `}
        </KText>
      </Tooltip>
      {isOpen && (
        <Box
          ref={popoverRef}
          position="fixed"
          zIndex={99999}
          bg="white"
          boxShadow="0px 4px 6px rgba(0, 0, 0, 0.1)"
          borderRadius="12px"
          minW="500px"
          maxW="500px"
          style={{
            top: position.top,
            left: position.left,
            right: position.right,
            maxHeight: 'calc(100vh - ' + position.top + 'px - 20px)',
            overflow: 'auto',
          }}
        >
          {renderTabs()}
        </Box>
      )}
    </Box>
  );
}
